import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import './LoginSignUp.css';
import google from './google.svg';
const LoginSignUp = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [addressData, setAddressData] = useState({
    name: '',
    locality: '',
    subLocality: '',
    district: '',
    pincode: '',
    phone: '',
  });
  const [errors, setErrors] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [userName, setUserName] = useState('');
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const navigate = useNavigate();
  const { search } = useLocation();

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const params = new URLSearchParams(search);
    const error = params.get('error');
    const message = params.get('message');
    if (error === 'auth_failed') {
      setErrors({
        form: message ? decodeURIComponent(message) : 'Google authentication failed. Please try again.',
      });
    }
  }, [search]);

  const handleGoogleLogin = () => {
    setIsGoogleLoading(true);
    window.location.href = `${API_BASE_URL}/auth/google`;
  };

  const toggleForm = () => {
    setIsLogin(!isLogin);
    setErrors({});
    setFormData({ name: '', email: '', password: '', confirmPassword: '' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: '' });
  };

  const handleAddressChange = (e) => {
    setAddressData({ ...addressData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: '' });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!isLogin && !formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.email.match(/^\S+@\S+\.\S+$/)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    if (!isLogin && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    return newErrors;
  };

  const validateAddressForm = () => {
    const newErrors = {};
    if (!addressData.name.trim()) newErrors.name = 'Name is required';
    if (!addressData.locality.trim()) newErrors.locality = 'Locality is required';
    if (!addressData.district.trim()) newErrors.district = 'District is required';
    if (!addressData.pincode.match(/^\d{6}$/)) newErrors.pincode = 'Pincode must be 6 digits';
    if (!addressData.phone.match(/^\d{10}$/)) newErrors.phone = 'Phone must be 10 digits';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsLoginLoading(true);
    try {
      const endpoint = isLogin ? '/api/auth/login' : '/api/auth/register';
      const payload = isLogin
        ? { email: formData.email, password: formData.password }
        : {
            name: formData.name.trim(),
            email: formData.email,
            password: formData.password,
          };

      const response = await axios.post(`${API_BASE_URL}${endpoint}`, payload);
      localStorage.setItem('token', response.data.token);

      if (!isLogin) {
        setUserName(response.data.name);
        setShowModal(true);
      } else {
        navigate('/');
      }
    } catch (err) {
      const message = err.response?.data?.message || 'An error occurred. Please try again.';
      setErrors({ form: message });
    } finally {
      setIsLoginLoading(false);
    }
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateAddressForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsLoginLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/api/auth/add-address`, addressData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      setShowModal(false);
      setAddressData({
        name: '',
        locality: '',
        subLocality: '',
        district: '',
        pincode: '',
        phone: '',
      });
      navigate('/');
    } catch (err) {
      setErrors({
        form: err.response?.data?.message || 'Failed to save address.',
      });
    } finally {
      setIsLoginLoading(false);
    }
  };

  return (
    <div className="auth-container w-100">
      <div className="auth-card">
        <div className="auth-header">
          <i className="bi bi-leaf-fill auth-icon"></i>
          <h2>{isLogin ? 'Login' : 'Sign Up'}</h2>
        </div>

        <div className="auth-toggle">
          <button className={isLogin ? 'toggle-btn active' : 'toggle-btn'} onClick={() => setIsLogin(true)}>
            Login
          </button>
          <button className={!isLogin ? 'toggle-btn active' : 'toggle-btn'} onClick={() => setIsLogin(false)}>
            Sign Up
          </button>
        </div>

        {errors.form && (
          <div className="auth-error">
            {errors.form}
          </div>
        )}

        <form className="auth-form" onSubmit={handleSubmit}>
          {!isLogin && (
            <div className="form-group">
              <p className="text-login"> Enter Your Name</p>
              <input
                type="text"
                name="name"
                placeholder=" Name"
                value={formData.name}
                onChange={handleChange}
                required
                className={errors.name ? 'input-error' : ''}
              />
              {errors.name && <span className="error-text">{errors.name}</span>}
            </div>
          )}

          <div className="form-group">
            <p className="text-login">Enter Your Email</p>
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleChange}
              required
              className={errors.email ? 'input-error' : ''}
            />
            {errors.email && <span className="error-text">{errors.email}</span>}
          </div>

          <div className="form-group">
            <p className="text-login">Enter Your Password</p>
            <input
              type="password"
              name="password"
              placeholder=" Password"
              value={formData.password}
              onChange={handleChange}
              required
              className={errors.password ? 'input-error' : ''}
            />
            {errors.password && <span className="error-text">{errors.password}</span>}
          </div>

          {!isLogin && (
            <div className="form-group">
              <p className="text-login">Confirm Your Password</p>
              <input
                type="password"
                name="confirmPassword"
                placeholder="Confirm Password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                className={errors.confirmPassword ? 'input-error' : ''}
              />
              {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
            </div>
          )}

      <button type="submit" className="submit-btn" disabled={isLoginLoading || isGoogleLoading}>
            {isLoginLoading ? (
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            ) : (
              isLogin ? 'Login' : 'Sign Up'
            )}
          </button>
      <button type="button" className="google-btn" onClick={handleGoogleLogin} disabled={isLoginLoading || isGoogleLoading}>
  {isGoogleLoading ? (
    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
  ) : (
  <>
      <img src={google} style={{maxWidth: '25px'}} alt="Google" />
      {isLogin ? 'Sign in with Google' : 'Sign up with Google'}
    </>
  )}
</button>


               </form>

        <br />
        <p className="auth-switch">
          {isLogin ? "Don't have an account?" : 'Already have an account?'}
          <span onClick={toggleForm} className="switch-link">
            {isLogin ? ' Sign Up' : ' Login'}
          </span>
        </p>

        <p className='text-muted small text-align-center fw-semibold'>By continuing, you agree to our Terms & Conditions.</p>
      </div>

      <div className={`modal fade ${showModal ? 'show d-block' : ''}`} tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Hello, {userName}!</h5>
              <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
            </div>
            <div className="modal-body">
              <p>Please provide your address details to continue.</p>
              {errors.form && <div className="alert alert-danger">{errors.form}</div>}

              <form onSubmit={handleAddressSubmit}>
                <div className="mb-3">
                  <label className="form-label">Name</label>
                  <input
                    type="text"
                    name="name"
                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                    value={addressData.name}
                    onChange={handleAddressChange}
                    required
                  />
                  {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                </div>

                <div className="mb-3">
                  <label className="form-label">Locality</label>
                  <input
                    type="text"
                    name="locality"
                    className={`form-control ${errors.locality ? 'is-invalid' : ''}`}
                    value={addressData.locality}
                    onChange={handleAddressChange}
                    required
                  />
                  {errors.locality && <div className="invalid-feedback">{errors.locality}</div>}
                </div>

                <div className="mb-3">
                  <label className="form-label">Sub-Locality/Road</label>
                  <input
                    type="text"
                    name="subLocality"
                    className="form-control"
                    value={addressData.subLocality}
                    onChange={handleAddressChange}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">District</label>
                  <input
                    type="text"
                    name="district"
                    className={`form-control ${errors.district ? 'is-invalid' : ''}`}
                    value={addressData.district}
                    onChange={handleAddressChange}
                    required
                  />
                  {errors.district && <div className="invalid-feedback">{errors.district}</div>}
                </div>

                <div className="mb-3">
                  <label className="form-label">Pincode</label>
                  <input
                    type="text"
                    name="pincode"
                    className={`form-control ${errors.pincode ? 'is-invalid' : ''}`}
                    value={addressData.pincode}
                    onChange={handleAddressChange}
                    required
                  />
                  {errors.pincode && <div className="invalid-feedback">{errors.pincode}</div>}
                </div>

                <div className="mb-3">
                  <label className="form-label">Phone Number</label>
                  <input
                    type="text"
                    name="phone"
                    className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                    value={addressData.phone}
                    onChange={handleAddressChange}
                    required
                  />
                  {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
                </div>

                <button type="submit" className="btn btn-success w-100" disabled={isLoginLoading}>
                  {isLoginLoading ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  ) : (
                    'Save Address'
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginSignUp;